import { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Box,
    Pagination,
    Select,
    MenuItem,
    FormControl,
    Chip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { goApi } from '../../../services';
import { LoadingSection } from '../../../common/LoadingSection';
import BatchTopicForm from './BatchTopicForm';
import moment from 'moment';

const BatchTopicsViewAction = ({ templateId, record, onRefresh }: any) => {
    const [open, setOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const notify = useNotify();

    const handleEditSuccess = () => {
        setOpen(false);
        if (onRefresh) onRefresh();
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await goApi.delete(`/batches/batch-topics/${record.id}`);
            notify('Batch topic deleted successfully', { type: 'success' });
            setDeleteOpen(false);
            if (onRefresh) onRefresh();
        } catch (err) {
            notify('Failed to delete batch topic', { type: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <div className="flex space-x-2">
                <Button onClick={() => setDeleteOpen(true)} color="error" size="small">
                    <DeleteIcon />
                </Button>
                <Button onClick={() => setOpen(true)} size="small">
                    <EditIcon />
                </Button>
            </div>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{`Edit Topic ${record?.title}`}</DialogTitle>
                <DialogContent>
                    <BatchTopicForm
                        record={record}
                        onCancel={() => setOpen(false)}
                        templateId={templateId}
                        onSuccess={handleEditSuccess}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete "{record?.title}"?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                        disabled={deleting}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export const BatchTopicsList = ({ batch }: { batch: any }) => {
    const [topics, setTopics] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [orderBy, setOrderBy] = useState<string>('createdAt');
    const [open, setOpen] = useState(false);
    const notify = useNotify();

    const batchId = batch?.id;
    const templateId = batch?.templateId;

    const fetchTopics = async () => {
        if (!batchId) return;

        setLoading(true);
        try {
            const response = await goApi.get(
                `/batches/batch-topics?batchId=${batchId}&page=${page}&limit=${limit}&orderBy=${orderBy}&order=${order.toUpperCase()}`,
            );
            setTopics(response.data?.items || []);
            setTotal(response.data?.pagination?.total || 0);
        } catch (error) {
            notify('Failed to fetch topics', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTopics();
    }, [page, limit, orderBy, order, batchId]);

    const handleCreateSuccess = () => {
        setOpen(false);
        fetchTopics();
    };

    const handleSort = (property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setPage(1);
    };

    const handlePageChange = (_: any, newPage: number) => {
        setPage(newPage);
    };

    const handleLimitChange = (event: any) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(1);
    };

    if (!batchId) {
        return <LoadingSection />;
    }

    if (loading) {
        return <LoadingSection />;
    }

    return (
        <>
            <div className="flex justify-between mb-2">
                <div />
                <Button onClick={() => setOpen(true)} variant="contained">
                    Add New
                </Button>
            </div>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead sx={{ backgroundColor: '#fafafa' }}>
                        <TableRow>
                            <TableCell sx={{ fontSize: '13px', fontWeight: 600 }}>
                                <TableSortLabel
                                    active={orderBy === 'title'}
                                    direction={orderBy === 'title' ? order : 'asc'}
                                    onClick={() => handleSort('title')}
                                >
                                    Title
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontSize: '13px', fontWeight: 600 }}>
                                <TableSortLabel
                                    active={orderBy === 'displayOrder'}
                                    direction={orderBy === 'displayOrder' ? order : 'asc'}
                                    onClick={() => handleSort('displayOrder')}
                                >
                                    Display Order
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontSize: '13px', fontWeight: 600 }}>
                                Created / Updated
                            </TableCell>
                            <TableCell sx={{ fontSize: '13px', fontWeight: 600 }}>
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {topics.map(topic => (
                            <TableRow key={topic.id} hover>
                                <TableCell sx={{ fontSize: '13px' }}>{topic.title}</TableCell>
                                <TableCell sx={{ fontSize: '13px' }}>
                                    {topic.displayOrder}
                                </TableCell>
                                <TableCell sx={{ fontSize: '13px' }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <Chip
                                                label="C"
                                                size="small"
                                                sx={{
                                                    backgroundColor: '#fff9c4',
                                                    color: '#000',
                                                    height: 20,
                                                    minWidth: 20,
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    '& .MuiChip-label': {
                                                        padding: '0 6px',
                                                    },
                                                }}
                                            />
                                            <span style={{ fontSize: 13 }}>
                                                {moment(topic.createdAt).format(
                                                    'DD-MMM-YYYY hh:mm A',
                                                )}
                                            </span>
                                        </Box>
                                        {topic.updatedAt && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <Chip
                                                    label="U"
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: '#e1bee7',
                                                        color: '#000',
                                                        height: 20,
                                                        minWidth: 20,
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        '& .MuiChip-label': {
                                                            padding: '0 6px',
                                                        },
                                                    }}
                                                />
                                                <span style={{ fontSize: 13 }}>
                                                    {moment(topic.updatedAt).format(
                                                        'DD-MMM-YYYY hh:mm A',
                                                    )}
                                                </span>
                                            </Box>
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ fontSize: '13px' }}>
                                    <BatchTopicsViewAction
                                        templateId={templateId}
                                        record={topic}
                                        onRefresh={fetchTopics}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '13px' }}>Rows per page:</span>
                    <FormControl size="small">
                        <Select
                            value={limit}
                            onChange={handleLimitChange}
                            sx={{ fontSize: '13px' }}
                        >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Pagination
                    count={Math.ceil(total / limit)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                />
            </Box>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Add Topic</DialogTitle>
                <DialogContent>
                    <BatchTopicForm
                        onCancel={() => setOpen(false)}
                        templateId={templateId}
                        onSuccess={handleCreateSuccess}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};
